// useGlobalShortcuts — wires window-level keyboard shortcuts (plan
// §P2.19 / audit §8.14).
//
// All callers can subscribe to the same hook so we have a single
// source of truth for "what does pressing X do globally". Inputs and
// textareas are ignored so the shortcuts never collide with typing.
//
// Today's shortcuts:
//
//   * ``?``      — open the keyboard cheat sheet overlay
//   * ``T``      — toggle dark / light theme
//   * ``g h``    — jump to /projects (Hub)
//   * ``g i``    — jump to /intake (Smart Intake)
//
// Command-Center–scoped shortcuts (``[`` / ``]`` / ``1..5``) live
// inside ``CommandCenterPage`` because they need access to the page
// state. They share the ``isEditableTarget`` helper so we never
// hijack typing.

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { applyTheme, useThemeStore } from '@/stores/themeStore'

/**
 * Returns true when the keyboard target is something the user is
 * actively typing into (input, textarea, contenteditable, or any
 * element marked ``data-shortcut-ignore``). We never fire global
 * shortcuts on those.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.dataset.shortcutIgnore === 'true') return true
  const tag = target.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (target.isContentEditable) return true
  return false
}

export interface UseGlobalShortcutsOpts {
  /** Open the keyboard cheat-sheet. */
  openShortcuts: () => void
}

export function useGlobalShortcuts({ openShortcuts }: UseGlobalShortcutsOpts) {
  const navigate = useNavigate()
  const toggleTheme = useThemeStore((s) => s.toggle)
  // Two-key chord state — stores the previous "g" press for ``g h`` /
  // ``g i`` etc. Times out after 1.2 s so a stale chord can't latch.
  const chord = useRef<{ key: string; at: number } | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Honor modifiers — never override Cmd/Ctrl/Alt combos.
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isEditableTarget(e.target)) return

      const key = e.key

      // ``?`` opens the cheat sheet (Shift+/ on US layouts).
      if (key === '?') {
        e.preventDefault()
        openShortcuts()
        return
      }

      // Two-key chord ``g h`` / ``g i``.
      const now = performance.now()
      if (chord.current && now - chord.current.at > 1200) {
        chord.current = null
      }
      if (chord.current?.key === 'g') {
        const next = key.toLowerCase()
        chord.current = null
        if (next === 'h') {
          e.preventDefault()
          navigate('/projects')
          return
        }
        if (next === 'i') {
          e.preventDefault()
          navigate('/intake')
          return
        }
      }
      if (key === 'g' || key === 'G') {
        chord.current = { key: 'g', at: now }
        return
      }

      // ``T`` toggles theme.
      if (key === 't' || key === 'T') {
        e.preventDefault()
        toggleTheme()
        // applyTheme is also subscribed to the store via the layout
        // effect, but call directly here so the change is instant
        // even before React reschedules the render.
        const next = useThemeStore.getState().theme
        applyTheme(next)
        return
      }
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navigate, openShortcuts, toggleTheme])
}
